/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { DatabaseBackend } from 'src/SqlLab/utils/databaseDialect';
import {
  quoteIdentifier,
  quoteQualifiedIdentifier,
} from 'src/SqlLab/utils/databaseDialect';
import type {
  JoinType,
  ModelNode,
  ModelRelationship,
  RelationshipModel,
} from '../types';
import { renderDatasetSubquery } from './datasetSubquery';
import type { CompileOptions, CompileResult, ValidationResult } from './types';

const JOIN_TYPE_SQL: Record<JoinType, string> = {
  inner: 'INNER',
  left: 'LEFT',
  right: 'RIGHT',
  full: 'FULL OUTER',
};

const ALIAS_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

export function datasetMacroAlias(datasetId: number): string {
  return `dataset_${datasetId}`;
}

export function assignTableAliases(nodes: ModelNode[]): Record<string, string> {
  const aliases: Record<string, string> = {};
  const used = new Set<string>();

  nodes.forEach(node => {
    if (node.kind === 'dataset' && node.ref.datasetId != null) {
      const macroAlias = datasetMacroAlias(node.ref.datasetId);
      aliases[node.id] = macroAlias;
      used.add(macroAlias);
      return;
    }

    if (node.customAlias && !used.has(node.customAlias)) {
      aliases[node.id] = node.customAlias;
      used.add(node.customAlias);
      return;
    }

    const key = `${node.ref.schema ?? ''}.${node.ref.table ?? node.id}`;
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash << 5) - hash + key.charCodeAt(i);
      hash &= hash;
    }
    let aliasIndex = Math.abs(hash) % ALIAS_LETTERS.length;
    let alias = ALIAS_LETTERS[aliasIndex];
    while (used.has(alias) && used.size < ALIAS_LETTERS.length) {
      aliasIndex = (aliasIndex + 1) % ALIAS_LETTERS.length;
      alias = ALIAS_LETTERS[aliasIndex];
    }
    aliases[node.id] = alias;
    used.add(alias);
  });

  return aliases;
}

export function topologicalSortRelationships(
  nodes: ModelNode[],
  relationships: ModelRelationship[],
): ModelRelationship[] {
  if (relationships.length === 0) {
    return relationships;
  }

  const nodeIds = new Set(nodes.map(n => n.id));
  const dependencies: Record<string, Set<string>> = {};
  nodes.forEach(n => {
    dependencies[n.id] = new Set();
  });

  relationships.forEach(rel => {
    if (nodeIds.has(rel.leftNodeId) && nodeIds.has(rel.rightNodeId)) {
      dependencies[rel.rightNodeId].add(rel.leftNodeId);
    }
  });

  const sorted: string[] = [];
  const visited = new Set<string>();
  const temp = new Set<string>();

  const visit = (nodeId: string) => {
    if (temp.has(nodeId)) {
      return;
    }
    if (visited.has(nodeId)) {
      return;
    }
    temp.add(nodeId);
    dependencies[nodeId]?.forEach(dep => visit(dep));
    temp.delete(nodeId);
    visited.add(nodeId);
    sorted.push(nodeId);
  };

  nodes.forEach(n => visit(n.id));

  const orderIndex: Record<string, number> = {};
  sorted.forEach((id, idx) => {
    orderIndex[id] = idx;
  });

  return [...relationships].sort((a, b) => {
    const aScore = orderIndex[a.rightNodeId] ?? 0;
    const bScore = orderIndex[b.rightNodeId] ?? 0;
    return aScore - bScore;
  });
}

export function validateRelationshipGraph(
  model: RelationshipModel,
): ValidationResult {
  const errors: ValidationResult['errors'] = [];
  const warnings: ValidationResult['warnings'] = [];
  const nodeIds = new Set(model.nodes.map(n => n.id));

  if (model.nodes.length === 0) {
    errors.push({
      code: 'NO_NODES',
      message: 'Relationship model has no nodes',
    });
    return { valid: false, errors, warnings };
  }

  model.nodes.forEach(node => {
    if (node.kind === 'physical_table' && !node.ref.table) {
      errors.push({
        code: 'MISSING_TABLE',
        message: 'Physical table node missing table name',
        nodeId: node.id,
      });
    }
    if (node.kind === 'dataset' && node.ref.datasetId == null) {
      errors.push({
        code: 'MISSING_DATASET',
        message: 'Dataset node missing datasetId',
        nodeId: node.id,
      });
    }
  });

  model.relationships.forEach(rel => {
    if (rel.leftNodeId === rel.rightNodeId) {
      errors.push({
        code: 'SELF_RELATIONSHIP',
        message: 'Relationship cannot connect a node to itself',
        relationshipId: rel.id,
      });
    }
    if (!nodeIds.has(rel.leftNodeId)) {
      errors.push({
        code: 'UNKNOWN_LEFT_NODE',
        message: `Relationship references unknown left node ${rel.leftNodeId}`,
        relationshipId: rel.id,
      });
    }
    if (!nodeIds.has(rel.rightNodeId)) {
      errors.push({
        code: 'UNKNOWN_RIGHT_NODE',
        message: `Relationship references unknown right node ${rel.rightNodeId}`,
        relationshipId: rel.id,
      });
    }
    if (!rel.keys.length) {
      errors.push({
        code: 'MISSING_KEYS',
        message: 'Relationship has no join keys',
        relationshipId: rel.id,
      });
    }
    rel.keys.forEach(key => {
      if (!key.leftColumn || !key.rightColumn) {
        errors.push({
          code: 'INCOMPLETE_KEY',
          message: 'Join key missing left or right column',
          relationshipId: rel.id,
        });
      }
    });
  });

  if (model.relationships.length === 0 && model.nodes.length > 1) {
    warnings.push({
      code: 'DISCONNECTED_NODES',
      message:
        'Multiple nodes without relationships; only FROM table will be used',
    });
  }

  const datasetIds = model.nodes
    .filter(n => n.kind === 'dataset' && n.ref.datasetId != null)
    .map(n => n.ref.datasetId as number);
  const dupIds = datasetIds.filter((id, idx) => datasetIds.indexOf(id) !== idx);
  if (dupIds.length > 0) {
    errors.push({
      code: 'DUPLICATE_DATASET_NODE',
      message: `Duplicate dataset nodes for datasetId(s): ${[
        ...new Set(dupIds),
      ].join(', ')}`,
    });
  }

  return { valid: errors.length === 0, errors, warnings };
}

function renderPhysicalTable(
  node: ModelNode,
  alias: string,
  backend?: DatabaseBackend,
): string {
  const quoted = quoteQualifiedIdentifier(
    node.ref.schema ?? '',
    node.ref.table ?? '',
    backend,
  );
  return `${quoted} ${alias}`;
}

function buildSelectClause(
  nodes: ModelNode[],
  aliases: Record<string, string>,
  includedNodeIds: Set<string>,
  backend?: DatabaseBackend,
): string {
  const parts: string[] = [];

  nodes.forEach(node => {
    if (!includedNodeIds.has(node.id)) {
      return;
    }
    const alias = aliases[node.id];
    const cols = node.outputColumns;
    if (cols && cols.length > 0) {
      cols.forEach(col => {
        parts.push(`${alias}.${quoteIdentifier(col, backend)}`);
      });
    } else {
      parts.push(`${alias}.*`);
    }
  });

  return parts.length > 0 ? parts.join(',\n  ') : '*';
}

export function buildFromClause(
  rootNode: ModelNode,
  alias: string,
  backend?: DatabaseBackend,
): string {
  if (rootNode.kind === 'dataset') {
    // dataset() macro already ends with "AS dataset_N"
    return renderDatasetSubquery(rootNode);
  }
  return renderPhysicalTable(rootNode, alias, backend);
}

export function buildJoinClauses(
  nodes: ModelNode[],
  relationships: ModelRelationship[],
  aliases: Record<string, string>,
  joinedNodeIds: Set<string>,
  backend?: DatabaseBackend,
): string[] {
  const nodeById = new Map(nodes.map(n => [n.id, n]));
  const clauses: string[] = [];

  const edgesByTarget: Record<string, ModelRelationship[]> = {};
  relationships.forEach(rel => {
    if (!edgesByTarget[rel.rightNodeId]) {
      edgesByTarget[rel.rightNodeId] = [];
    }
    edgesByTarget[rel.rightNodeId].push(rel);
  });

  Object.entries(edgesByTarget).forEach(([targetId, relsForTarget]) => {
    const targetNode = nodeById.get(targetId);
    if (!targetNode || joinedNodeIds.has(targetId)) {
      return;
    }

    const firstRel = relsForTarget[0];
    const joinType = JOIN_TYPE_SQL[firstRel.joinType] ?? 'INNER';
    const targetAlias = aliases[targetId];

    const onConditions: string[] = [];
    relsForTarget.forEach(rel => {
      const leftAlias = aliases[rel.leftNodeId];
      rel.keys.forEach(key => {
        onConditions.push(
          `${leftAlias}.${quoteIdentifier(
            key.leftColumn,
            backend,
          )} = ${targetAlias}.${quoteIdentifier(key.rightColumn, backend)}`,
        );
      });
    });

    if (onConditions.length === 0) {
      return;
    }

    let rightTableSql: string;
    let joinTargetSuffix: string;
    if (targetNode.kind === 'dataset') {
      rightTableSql = renderDatasetSubquery(targetNode);
      joinTargetSuffix = '';
    } else {
      rightTableSql = quoteQualifiedIdentifier(
        targetNode.ref.schema ?? '',
        targetNode.ref.table ?? '',
        backend,
      );
      joinTargetSuffix = ` ${targetAlias}`;
    }

    clauses.push(
      `${joinType} JOIN ${rightTableSql}${joinTargetSuffix}\n        ON ${onConditions.join(
        '\n  AND ',
      )}`,
    );
    joinedNodeIds.add(targetId);
  });

  return clauses;
}

export function compileJoinSql(
  model: RelationshipModel,
  options: CompileOptions = {},
): CompileResult {
  const validation = validateRelationshipGraph(model);
  if (!validation.valid) {
    return { sql: '', validation };
  }

  const { backend, trailingSemicolon = true } = options;
  const aliases = assignTableAliases(model.nodes);
  const sortedRels = topologicalSortRelationships(
    model.nodes,
    model.relationships,
  );

  let rootNode = model.nodes[0];
  if (sortedRels.length > 0) {
    const rootCandidate = model.nodes.find(
      n => n.id === sortedRels[0].leftNodeId,
    );
    if (rootCandidate) {
      rootNode = rootCandidate;
    }
  }

  const joinedNodeIds = new Set<string>();
  const joinClauses = buildJoinClauses(
    model.nodes,
    sortedRels,
    aliases,
    joinedNodeIds,
    backend,
  );

  const includedNodeIds = new Set<string>([rootNode.id]);
  joinedNodeIds.forEach(id => includedNodeIds.add(id));

  const selectClause = buildSelectClause(
    model.nodes,
    aliases,
    includedNodeIds,
    backend,
  );
  const fromClause = buildFromClause(rootNode, aliases[rootNode.id], backend);

  const sql = `SELECT
  ${selectClause}
FROM ${fromClause}
${joinClauses.length > 0 ? joinClauses.join('\n') : ''}${
    trailingSemicolon ? ';' : ''
  }`;

  return { sql: sql.trim(), validation };
}

/** @deprecated Use compileJoinSql */
export function compileJoinSqlLegacy(
  model: RelationshipModel,
  options?: CompileOptions,
): string {
  return compileJoinSql(model, options).sql;
}
